import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { formatDateTime, formatPhone } from '../../lib/format';

interface Submission {
  playerId: string;
  name: string;
  email: string;
  phone: string | null;
  submitted: boolean;
  bonusAwarded: boolean;
  firstSubmittedAt: string | null;
  lastUpdatedAt: string | null;
  tiebreakerPrediction: number | null;
}

export function SubmissionsPanel({ weekId }: { weekId: string }) {
  const [subs, setSubs] = useState<Submission[]>([]);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<Submission | null>(null);

  async function load() {
    try {
      const res = await api.get<{ submissions: Submission[] }>(
        `/admin/weeks/${weekId}/submissions`,
      );
      setSubs(res.submissions);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Unable to load submissions.');
    }
  }

  useEffect(() => {
    load();
  }, [weekId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return subs;
    const qDigits = q.replace(/\D/g, '');
    return subs.filter((s) => {
      const nameEmail = `${s.name} ${s.email}`.toLowerCase().includes(q);
      const phoneDigits = (s.phone ?? '').replace(/\D/g, '');
      const phoneMatch =
        qDigits.length > 0 && (phoneDigits.includes(qDigits) || formatPhone(s.phone).toLowerCase().includes(q));
      return nameEmail || phoneMatch;
    });
  }, [subs, query]);

  const submittedCount = subs.filter((s) => s.submitted).length;

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-2xl uppercase text-ink">Submissions</h3>
        <span className="text-sm text-muted">
          {submittedCount} / {subs.length} submitted
        </span>
      </div>
      {error && <p className="mb-2 text-sm text-danger">{error}</p>}

      <input
        className="input mb-3"
        placeholder="Search by name, email, or phone"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-2 py-2">Player</th>
              <th className="px-2 py-2">Phone</th>
              <th className="px-2 py-2">Status</th>
              <th className="px-2 py-2">Updated</th>
              <th className="px-2 py-2">Bonus</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.playerId} className="border-b border-line last:border-0">
                <td className="px-2 py-2">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-muted">{s.email}</div>
                </td>
                <td className="px-2 py-2 text-muted">{formatPhone(s.phone) || '—'}</td>
                <td className="px-2 py-2">
                  {s.submitted ? (
                    <span className="chip bg-success/15 text-success">Submitted</span>
                  ) : (
                    <span className="chip bg-line text-muted">Not yet</span>
                  )}
                </td>
                <td className="px-2 py-2 text-muted">{formatDateTime(s.lastUpdatedAt)}</td>
                <td className="px-2 py-2">
                  {s.bonusAwarded ? (
                    <span className="chip bg-amber/20 text-amber-dark">Bonus ✓</span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  {s.submitted && (
                    <button
                      className={s.bonusAwarded ? 'text-danger underline' : 'text-grove underline'}
                      onClick={() => setPending(s)}
                    >
                      {s.bonusAwarded ? 'Remove bonus' : 'Give bonus'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-2 py-4 text-center text-muted">
                  No players match “{query}”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pending && (
        <BonusModal
          weekId={weekId}
          submission={pending}
          onClose={() => setPending(null)}
          onDone={() => {
            setPending(null);
            load();
          }}
        />
      )}
    </Card>
  );
}

function BonusModal({
  weekId,
  submission,
  onClose,
  onDone,
}: {
  weekId: string;
  submission: Submission;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const removing = submission.bonusAwarded;

  async function confirm() {
    setBusy(true);
    setError('');
    try {
      const path = `/admin/weeks/${weekId}/submissions/${submission.playerId}/bonus`;
      if (removing) await api.del(path);
      else await api.post(path);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Unable to update bonus.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 p-4">
      <div className="w-full max-w-sm rounded-lg bg-surface p-5 shadow-pop">
        <h3 className="font-display text-2xl uppercase text-ink">
          {removing ? 'Remove bonus' : 'Give bonus'}
        </h3>
        <p className="mt-2 text-sm text-muted">
          {removing ? 'Remove the weekly bonus from' : 'Give the weekly bonus to'}
        </p>
        <p className="my-2 text-center font-display text-3xl text-grove">{submission.name}</p>
        {error && <p className="mt-2 text-sm text-danger">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" onClick={confirm} disabled={busy}>
            {busy ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
